import React from 'react';
import { useParams } from 'react-router-dom';
import { useAccessGroups } from './ResourcesRouter.js';
import {
	Card,
	CardBody,
	CardHeader,
	Button,
	Input,
	Switch,
	RadioGroup,
	Radio,
	Skeleton,
	Divider,
	Select,
	SelectItem,
	Chip,
	addToast
} from '@heroui/react';
import {
	Scale,
	Save,
	Plus,
	Trash2,
	Plane,
	Edit,
	Eye,
	Upload,
	Image as ImageIcon,
	X,
	Fuel,
	ImagePlus
} from 'lucide-react';
import ResourcesApi from '../../api/resourcesApi.js';
import HeroUIBreadcrumb from '../breadcrumbs/HeroUIBreadcrumb.js';
import { UnsavedChangesGuard, useUnsavedChangesGuard } from './components/UnsavedChangesGuard.js';
import { extractApiError } from './utils/extractApiError.js';

// Get organization unique from URL
const getOrgUnique = () => {
	const match = window.location.pathname.match(/\/org\/([^/]+)\//);
	return match ? match[1] : '';
};

function WeightBalancePageContent() {
	const { aircraftId } = useParams();
	const { setDirty, navigateWithCheck } = useUnsavedChangesGuard();
	const accessGroups = useAccessGroups();
	const canManage = accessGroups._loaded && !!accessGroups.resourcesManage;
	const orgUnique = getOrgUnique();
	const fileInputRef = React.useRef(null);

	const [aircraft, setAircraft] = React.useState(null);
	const [loading, setLoading] = React.useState(true);
	const [saving, setSaving] = React.useState(false);
	const isInitializedRef = React.useRef(false);

	// Weight & Balance form state
	// Note: Only basicEmptyWeight has an arm field - max weights don't have arm
	const [formData, setFormData] = React.useState({
		basicEmptyWeight: '',
		basicEmptyArm: '',
		maxRampWeight: '',
		maxTakeoffWeight: '',
		maxLandingWeight: '',
		rows: []
	});

	// Fuel state
	const [fuelData, setFuelData] = React.useState({
		consumption: '',
		density: '6.0',
		reserve: '30',
		tankType: 'static',
		pilotRight: false,
		capacity: '',
		arm: '',
		slidingFuelTankId: null
	});

	// Fuel tank models for dynamic/sliding tank dropdown
	const [fuelTankModels, setFuelTankModels] = React.useState([]);

	// Envelope image state
	const [envelopeImage, setEnvelopeImage] = React.useState(null);
	const [existingImageUrl, setExistingImageUrl] = React.useState(null);
	const [previewBlobUrl, setPreviewBlobUrl] = React.useState(null);
	const [nextRowId, setNextRowId] = React.useState(1);

	// Create and cleanup blob URL for image preview (avoid memory leaks)
	React.useEffect(() => {
		if (envelopeImage) {
			const url = URL.createObjectURL(envelopeImage);
			setPreviewBlobUrl(url);
			return () => URL.revokeObjectURL(url);
		} else {
			setPreviewBlobUrl(null);
		}
	}, [envelopeImage]);

	// Fetch aircraft data
	const fetchAircraftData = React.useCallback(async () => {
		if (!aircraftId) return;
		setLoading(true);
		try {
			const response = await ResourcesApi.getAircraft(parseInt(aircraftId, 10));
			const data = response.data?.data || response.data || response;
			setAircraft(data);

			// Initialize form with aircraft weight and balance data
			const wb = data.weightAndBalance || data.balance || data;
			const rows = wb.rows || wb.seats || data.seats || [];

			// basicEmptyWeight is an object { weight, arm } in the API
			const basicEmpty = wb.basicEmptyWeight || data.basicEmptyWeight || {};
			const basicEmptyWeight = typeof basicEmpty === 'object' ? basicEmpty.weight : basicEmpty;
			const basicEmptyArm = typeof basicEmpty === 'object' ? basicEmpty.arm : (wb.basicEmptyArm || data.basicEmptyArm);

			setFormData({
				basicEmptyWeight: basicEmptyWeight || '',
				basicEmptyArm: basicEmptyArm || '',
				// Max weights have NO arm field (only weight)
				maxRampWeight: wb.maxRampWeight || data.maxRampWeight || '',
				maxTakeoffWeight: wb.maxTakeoffWeight || data.maxTakeoffWeight || '',
				maxLandingWeight: wb.maxLandingWeight || data.maxLandingWeight || '',
				rows: rows.length > 0
					? rows.map((r, idx) => ({
							id: idx + 1,
							name: r.name || '',
							seats: r.seats ?? r.count ?? 0,
							arm: r.arm || ''
						}))
					: []
			});

			// Initialize next row ID
			setNextRowId(rows.length + 1);

			// Initialize fuel data
			// Note: API uses fuelTankType: 'static' | 'sliding', we store as tankType: 'static' | 'dynamic'
			const apiFuelTankType = wb.fuelTankType || data.fuelTankType;
			let tankType = 'static'; // default to static
			if (apiFuelTankType === 'sliding') {
				tankType = 'dynamic';
			} else if (apiFuelTankType === 'static') {
				tankType = 'static';
			} else if (wb.isStaticFuelTank || data.isStaticFuelTank) {
				// Legacy fallback
				tankType = 'static';
			} else if (apiFuelTankType) {
				// Unknown type, default to static
				tankType = 'static';
			}
			const staticTank = wb.staticFuelTank || data.staticFuelTank || {};

			// Extract slidingFuelTankId - API may return it as object or direct ID
			const slidingTank = wb.slidingFuelTank || data.slidingFuelTank;
			const slidingFuelTankId =
				wb.slidingFuelTankId ||
				data.slidingFuelTankId ||
				(slidingTank?.id) ||
				(typeof slidingTank === 'number' ? slidingTank : null);

			setFuelData({
				consumption: wb.fuelConsumption || data.fuelConsumption || '',
				density: wb.fuelDensity || data.fuelDensity || '6.0',
				reserve: wb.fuelReserveMin || data.fuelReserveMin || '30',
				tankType: tankType,
				pilotRight: wb.pilotRight || data.pilotRight || false,
				capacity: staticTank.capacity || wb.fuelCapacity || data.fuelCapacity || '',
				arm: staticTank.arm || wb.fuelArm || data.fuelArm || '',
				slidingFuelTankId: slidingFuelTankId
			});

			// Set existing envelope image URL if available
			// Image is stored on the resource, not on weightAndBalance
			const spacesUrl = typeof window !== 'undefined' ? window.serverData?._spaces_url : '';
			const resourceImage = data.resources?.[0]?.image || data.resources?.[0]?.originalImage;
			if (resourceImage) {
				// Ensure proper URL: spacesUrl may not have trailing slash, resourceImage may not have leading slash
				const separator = spacesUrl && !spacesUrl.endsWith('/') && !resourceImage.startsWith('/') ? '/' : '';
				const fullUrl = resourceImage.startsWith('http') ? resourceImage : `${spacesUrl}${separator}${resourceImage}`;
				setExistingImageUrl(fullUrl);
			} else if (wb.envelopeImage || data.envelopeImage) {
				// Fallback for legacy data
				setExistingImageUrl(wb.envelopeImage || data.envelopeImage);
			}
		} catch (err) {
			console.error('Failed to load aircraft:', err);
			addToast({
				title: 'Error',
				description: 'Failed to load aircraft data',
				color: 'danger'
			});
		} finally {
			setLoading(false);
			// Mark as initialized after first load - only then allow dirty tracking
			setTimeout(() => {
				isInitializedRef.current = true;
			}, 100);
		}
	}, [aircraftId]);

	React.useEffect(() => {
		fetchAircraftData();
	}, [fetchAircraftData]);

	// Fetch fuel tank models for dynamic/sliding tank dropdown
	React.useEffect(() => {
		const fetchFuelTankModels = async () => {
			try {
				const response = await ResourcesApi.getAllFuelTanks();
				const tanks = response.data?.data || response.data || response || [];
				setFuelTankModels(Array.isArray(tanks) ? tanks : []);
			} catch (err) {
				console.warn('Failed to load fuel tank models:', err);
				setFuelTankModels([]);
			}
		};
		fetchFuelTankModels();
	}, []);

	// Auto-select first tank model only when user manually switches to dynamic (not during initial load)
	React.useEffect(() => {
		if (
			isInitializedRef.current &&
			fuelData.tankType === 'dynamic' &&
			!fuelData.slidingFuelTankId &&
			fuelTankModels.length > 0
		) {
			setFuelData(prev => ({ ...prev, slidingFuelTankId: fuelTankModels[0].id }));
		}
	}, [fuelData.tankType, fuelData.slidingFuelTankId, fuelTankModels]);

	// Handle weight balance field changes
	const handleWeightBalanceChange = (field, subfield, value) => {
		setFormData(prev => ({
			...prev,
			[field + (subfield === 'weight' ? '' : 'Arm')]: value
		}));
		if (isInitializedRef.current) setDirty(true);
	};

	// Handle weight balance changes (for single fields)
	const handleFieldChange = (field, value) => {
		setFormData(prev => ({ ...prev, [field]: value }));
		if (isInitializedRef.current) setDirty(true);
	};

	// Handle fuel field changes
	const handleFuelChange = (field, value) => {
		setFuelData(prev => ({ ...prev, [field]: value }));
		if (isInitializedRef.current) setDirty(true);
	};

	// Handle row changes
	const handleRowChange = (id, field, value) => {
		setFormData(prev => ({
			...prev,
			rows: prev.rows.map(row => (row.id === id ? { ...row, [field]: value } : row))
		}));
		if (isInitializedRef.current) setDirty(true);
	};

	// Check if value is not a valid number
	const isInvalidNumber = value => {
		if (!value || value === '') return false;
		const str = String(value).trim();
		// Allow: digits, one decimal point, optional leading minus
		return !/^-?\d*\.?\d*$/.test(str) || str === '.' || str === '-';
	};

	// Check if value has more than 2 decimal places
	const hasInvalidDecimals = value => {
		if (!value || value === '') return false;
		const str = String(value);
		const decimalIndex = str.indexOf('.');
		if (decimalIndex === -1) return false;
		const decimals = str.slice(decimalIndex + 1);
		return decimals.length > 2;
	};

	// Check if value is empty (required field validation)
	const isEmpty = value => !value || String(value).trim() === '';

	// Get error message for a numeric field (with optional required check)
	const getFieldError = (value, isRequired = false) => {
		if (isRequired && isEmpty(value)) return 'This field is required';
		if (isInvalidNumber(value)) return 'Must be a valid number';
		if (hasInvalidDecimals(value)) return 'Max 2 decimal places';
		return null;
	};

	// Check if field has any validation error (with optional required check)
	const hasFieldError = (value, isRequired = false) =>
		(isRequired && isEmpty(value)) || isInvalidNumber(value) || hasInvalidDecimals(value);

	// Names (non-empty) that appear on more than one row.
	// Exact raw-string comparison — mirrors backend array_unique over SeatRequestDto::getName().
	const getDuplicateRowNames = () => {
		const nameCounts = {};
		formData.rows.forEach(row => {
			if (!isEmpty(row.name)) {
				nameCounts[row.name] = (nameCounts[row.name] || 0) + 1;
			}
		});
		return new Set(Object.keys(nameCounts).filter(name => nameCounts[name] > 1));
	};

	// Check if any weight/balance field has validation errors
	const hasValidationErrors = () => {
		// Check required form data fields (all are required per Symfony API)
		const requiredFormFields = [
			formData.basicEmptyWeight,
			formData.basicEmptyArm,
			formData.maxRampWeight,
			formData.maxTakeoffWeight,
			formData.maxLandingWeight
		];
		if (requiredFormFields.some(val => hasFieldError(val, true))) return true;

		// Check row fields - if rows exist, name and arm are required, and names must be unique
		const duplicateRowNames = getDuplicateRowNames();
		if (formData.rows.some(row => isEmpty(row.name) || duplicateRowNames.has(row.name) || hasFieldError(row.arm, true))) return true;

		// Check required fuel fields
		const requiredFuelFields = [fuelData.consumption, fuelData.density];
		if (requiredFuelFields.some(val => hasFieldError(val, true))) return true;

		// Check static fuel tank fields (required only when tankType is static)
		if (fuelData.tankType === 'static') {
			if (hasFieldError(fuelData.capacity, true) || hasFieldError(fuelData.arm, true)) return true;
		}

		return false;
	};

	// Add new row
	const handleAddRow = () => {
		const newRow = {
			id: nextRowId,
			name: '',
			seats: 1,
			arm: ''
		};
		setFormData(prev => ({
			...prev,
			rows: [...prev.rows, newRow]
		}));
		setNextRowId(prev => prev + 1);
		setDirty(true);
	};

	// Remove row
	const handleRemoveRow = id => {
		setFormData(prev => ({
			...prev,
			rows: prev.rows.filter(row => row.id !== id)
		}));
		setDirty(true);
	};

	// Handle image upload
	const handleImageUpload = event => {
		const file = event.target.files[0];
		if (file) {
			if (file.size > 5 * 1024 * 1024) {
				addToast({
					title: 'Error',
					description: 'Image size must be less than 5MB',
					color: 'danger'
				});
				return;
			}
			setEnvelopeImage(file);
			setExistingImageUrl(null);
			setDirty(true);
		}
	};

	// Handle drag and drop
	const handleDragOver = e => {
		e.preventDefault();
		e.stopPropagation();
		e.currentTarget.classList.add('border-primary-500', 'bg-primary-50');
	};

	const handleDragLeave = e => {
		e.preventDefault();
		e.stopPropagation();
		e.currentTarget.classList.remove('border-primary-500', 'bg-primary-50');
	};

	const handleDrop = e => {
		e.preventDefault();
		e.stopPropagation();
		e.currentTarget.classList.remove('border-primary-500', 'bg-primary-50');

		if (e.dataTransfer.files && e.dataTransfer.files[0]) {
			const file = e.dataTransfer.files[0];
			if (file.type.startsWith('image/')) {
				handleImageUpload({ target: { files: [file] } });
			}
		}
	};

	// Remove image (from local state and server if exists)
	const handleRemoveImage = async () => {
		const resourceId = aircraft?.resources?.[0]?.id;

		// If there's an existing image on server, delete it
		if (existingImageUrl && resourceId) {
			try {
				await ResourcesApi.deleteResourceImage(resourceId);
				addToast({
					title: 'Success',
					description: 'Image deleted successfully',
					color: 'success'
				});
			} catch (err) {
				console.error('Failed to delete image:', err);
				addToast({
					title: 'Error',
					description: 'Failed to delete image',
					color: 'danger'
				});
				return; // Don't clear local state if server delete failed
			}
		}

		setEnvelopeImage(null);
		setExistingImageUrl(null);
	};

	// Save weight and balance settings
	const handleSave = async () => {
		setSaving(true);
		try {
			// Prepare data for API
			// Note: API expects specific structure for fuel tank data
			// - fuelTankType: 'static' | 'sliding' (not boolean)
			// - staticFuelTank: { capacity, arm } object for static
			// - slidingFuelTankId: number for sliding/dynamic
			const isStatic = fuelData.tankType === 'static';
			const payload = {
				basicEmptyWeight: {
					weight: formData.basicEmptyWeight || '',
					arm: formData.basicEmptyArm || ''
				},
				maxRampWeight: formData.maxRampWeight || '',
				maxTakeoffWeight: formData.maxTakeoffWeight || '',
				maxLandingWeight: formData.maxLandingWeight || '',
				fuelConsumption: fuelData.consumption || '',
				fuelDensity: fuelData.density || '6.0',
				fuelReserveMin: fuelData.reserve || null,
				fuelTankType: isStatic ? 'static' : 'sliding',
				pilotRight: fuelData.pilotRight ? 1 : null,
				seats: formData.rows.map(r => ({
					name: r.name,
					seats: parseInt(r.seats, 10) || 0,
					arm: r.arm || ''
				})),
				weightUnit: 'lbs'
			};

			// Add tank-specific data
			if (isStatic) {
				payload.staticFuelTank = {
					capacity: fuelData.capacity || '',
					arm: fuelData.arm || ''
				};
			} else {
				payload.slidingFuelTankId = fuelData.slidingFuelTankId || null;
			}

			await ResourcesApi.saveWeightAndBalance(parseInt(aircraftId, 10), payload);

			// Upload envelope image if a new one was selected
			const resourceId = aircraft?.resources?.[0]?.id;
			let imageWasUploaded = false;
			if (envelopeImage && resourceId) {
				try {
					const imageResponse = await ResourcesApi.uploadResourceImage(resourceId, envelopeImage);
					// Update existing image URL from response
					const respData = imageResponse.data?.data || imageResponse.data || imageResponse;
					const imageUrl = respData?.originalImage || respData?.bigImage || respData?.mediumImage;
					if (imageUrl) {
						setExistingImageUrl(imageUrl);
						imageWasUploaded = true;
					}
					setEnvelopeImage(null); // Clear the file after successful upload
				} catch (err) {
					console.error('Failed to upload image:', err);
					addToast({
						title: 'Warning',
						description: 'W&B settings saved, but image upload failed: ' + (err.message || 'Unknown error'),
						color: 'warning'
					});
					return; // Don't show success toast if image failed
				}
			}

			// Only refetch if no image was uploaded (image upload already set the correct URL)
			// Temporarily disable dirty tracking during refetch to prevent race conditions
			isInitializedRef.current = false;
			if (!imageWasUploaded) {
				await fetchAircraftData();
				// fetchAircraftData() restores isInitializedRef after 100ms
			} else {
				// Restore dirty tracking after brief delay (matches fetchAircraftData behavior)
				setTimeout(() => {
					isInitializedRef.current = true;
				}, 100);
			}
			setDirty(false);

			addToast({
				title: 'Success',
				description: 'Weight & Balance settings saved successfully',
				color: 'success'
			});
		} catch (err) {
			console.error('Failed to save:', err);
			const status = err.response?.status;
			const isClientError = status >= 400 && status < 500;
			addToast({
				title: 'Error',
				description: isClientError
					? extractApiError(err, 'Please fill in all required fields')
					: 'Failed to save Weight & Balance settings',
				color: 'danger'
			});
		} finally {
			setSaving(false);
		}
	};

	// Navigate to envelope editor
	const handleEditEnvelope = () => {
		navigateWithCheck(`/aircraft/${aircraftId}/envelope`);
	};

	const handleEditFuelTank = () => {
		navigateWithCheck(`/aircraft/${aircraftId}/fuel-tank`);
	};

	// Back navigation
	const handleBack = () => {
		navigateWithCheck(`/aircraft/${aircraftId}`);
	};

	// Breadcrumbs
	const breadcrumbCustomItems = [
		{ label: window.location.pathname.includes('/hangars') ? 'Aircraft Status' : 'Resources', href: `/org/${orgUnique}/${window.location.pathname.includes('/hangars') ? 'hangars' : 'resources'}/` },
		{ label: aircraft?.name || 'Aircraft', href: `/org/${orgUnique}/resources/aircraft/${aircraftId}` }
	];

	// Get image preview URL (uses cached blob URL to avoid memory leaks)
	const getImagePreview = () => {
		return previewBlobUrl || existingImageUrl || null;
	};

	// Loading skeleton
	if (loading) {
		return (
			<div className="max-w-[1400px] mx-auto px-4 py-8">
				<Skeleton className="h-8 w-48 rounded-lg mb-6" />
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
					<Skeleton className="h-96 w-full rounded-lg" />
					<div className="space-y-6">
						<Skeleton className="h-48 w-full rounded-lg" />
						<Skeleton className="h-48 w-full rounded-lg" />
					</div>
				</div>
			</div>
		);
	}

	const imagePreview = getImagePreview();
	const duplicateRowNames = getDuplicateRowNames();

	return (
		<div className="max-w-[1400px] mx-auto px-4 py-8">
			<HeroUIBreadcrumb currentPage="Weight & Balance" customItems={breadcrumbCustomItems} />

			{/* Page Header */}
			<div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
				<div className="flex items-center gap-4">
					<div className="p-3 rounded-full bg-gradient-to-br from-slate-500 to-gray-700">
						<Scale className="w-8 h-8 text-white" />
					</div>
					<div>
						<h1 className="text-2xl font-bold">{aircraft?.name || 'Aircraft'}</h1>
						<p className="text-default-500 text-sm">
							{aircraft?.registrationNumber}
							{(aircraft?.manufacturer || aircraft?.model) && ' · '}
							{[aircraft?.manufacturer, aircraft?.model].filter(Boolean).join(' ')}
							{' · Weight & Balance'}
						</p>
					</div>
				</div>
				<div className="self-end md:self-auto">
					<Button variant="light" isIconOnly onPress={handleBack}>
						<X className="w-5 h-5" />
					</Button>
				</div>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
				{/* Left Column: Weight & Balance Card (Slate gradient) */}
				<Card className="border border-default-200 shadow-md bg-gradient-to-br from-white to-slate-50/20">
					<CardHeader className="bg-gradient-to-r from-slate-100/80 via-slate-50/50 to-transparent pb-3 border-b border-slate-100/50">
						<div className="flex items-center gap-3">
							<div className="p-2 rounded-lg bg-slate-500/10 ring-1 ring-slate-500/20">
								<Scale className="w-5 h-5 text-slate-600" />
							</div>
							<span className="text-lg font-semibold text-slate-900">Weight & Balance</span>
						</div>
					</CardHeader>
					<CardBody className="pt-4 space-y-5">
						{/* Section: Weight Parameters */}
						<div>
							<div className="mb-5">
								<h4 className="text-xs font-semibold text-default-400 uppercase tracking-wider flex items-center gap-2">
									<span className="w-1 h-4 bg-slate-400 rounded-full" />
									Weight Parameters
								</h4>
							</div>
							<div className="space-y-3">
								{/* Basic Empty Weight - has Arm */}
								<div className="grid grid-cols-3 gap-2 items-start">
									<div className="text-sm text-default-700 pt-2">Basic Empty Weight <span className="text-danger">*</span></div>
									<Input
										size="sm"
										isReadOnly={!canManage}
										type="text"
										inputMode="decimal"
										placeholder="Weight (lbs)"
										value={formData.basicEmptyWeight}
										onValueChange={val => handleFieldChange('basicEmptyWeight', val)}
										isInvalid={hasFieldError(formData.basicEmptyWeight, true)}
										errorMessage={getFieldError(formData.basicEmptyWeight, true)}
										isRequired
										variant="bordered"
									/>
									<Input
										size="sm"
										isReadOnly={!canManage}
										type="text"
										inputMode="decimal"
										placeholder="Arm (in)"
										value={formData.basicEmptyArm}
										onValueChange={val => handleFieldChange('basicEmptyArm', val)}
										isInvalid={hasFieldError(formData.basicEmptyArm, true)}
										errorMessage={getFieldError(formData.basicEmptyArm, true)}
										isRequired
										variant="bordered"
									/>
								</div>

								{/* Maximum Ramp Weight - NO Arm */}
								<div className="grid grid-cols-3 gap-2 items-start">
									<div className="text-sm text-default-700 pt-2">Max Ramp Weight <span className="text-danger">*</span></div>
									<Input
										size="sm"
										isReadOnly={!canManage}
										type="text"
										inputMode="decimal"
										placeholder="Weight (lbs)"
										value={formData.maxRampWeight}
										onValueChange={val => handleFieldChange('maxRampWeight', val)}
										isInvalid={hasFieldError(formData.maxRampWeight, true)}
										errorMessage={getFieldError(formData.maxRampWeight, true)}
										isRequired
										variant="bordered"
									/>
									<div></div>
								</div>

								{/* Maximum Takeoff Weight - NO Arm */}
								<div className="grid grid-cols-3 gap-2 items-start">
									<div className="text-sm text-default-700 pt-2">Max Takeoff Weight <span className="text-danger">*</span></div>
									<Input
										size="sm"
										isReadOnly={!canManage}
										type="text"
										inputMode="decimal"
										placeholder="Weight (lbs)"
										value={formData.maxTakeoffWeight}
										onValueChange={val => handleFieldChange('maxTakeoffWeight', val)}
										isInvalid={hasFieldError(formData.maxTakeoffWeight, true)}
										errorMessage={getFieldError(formData.maxTakeoffWeight, true)}
										isRequired
										variant="bordered"
									/>
									<div></div>
								</div>

								{/* Maximum Landing Weight - NO Arm */}
								<div className="grid grid-cols-3 gap-2 items-start">
									<div className="text-sm text-default-700 pt-2">Max Landing Weight <span className="text-danger">*</span></div>
									<Input
										size="sm"
										isReadOnly={!canManage}
										type="text"
										inputMode="decimal"
										placeholder="Weight (lbs)"
										value={formData.maxLandingWeight}
										onValueChange={val => handleFieldChange('maxLandingWeight', val)}
										isInvalid={hasFieldError(formData.maxLandingWeight, true)}
										errorMessage={getFieldError(formData.maxLandingWeight, true)}
										isRequired
										variant="bordered"
									/>
									<div></div>
								</div>
							</div>
						</div>

						<Divider className="my-4" />

						{/* Section: Passenger & Cargo Rows */}
						<div>
							<div className="flex items-center justify-between mb-5">
								<h4 className="text-xs font-semibold text-default-400 uppercase tracking-wider flex items-center gap-2">
									<span className="w-1 h-4 bg-slate-400 rounded-full" />
									Passenger & Cargo Rows
								</h4>
								{canManage && <Button
									color="success"
									variant="flat"
									startContent={<Plus className="w-4 h-4 shrink-0" />}
									onPress={handleAddRow}
									size="sm"
								>
									Add Row
								</Button>}
							</div>

							{formData.rows.length === 0 ? (
								<div className="text-center py-6 text-default-400 bg-slate-50/50 rounded-lg border border-dashed border-default-200">
									<Plane className="w-10 h-10 mx-auto mb-2 opacity-40" />
									<p className="text-sm">No rows configured</p>
									<p className="text-xs">Add passenger and cargo stations</p>
								</div>
							) : (
								<div className="space-y-3">
									{formData.rows.map((row, index) => (
										<div key={row.id} className="p-3 bg-slate-50/50 rounded-lg border border-slate-100">
											<div className="flex items-center justify-between mb-2">
												<span className="text-sm font-medium text-slate-700">Row {index + 1}</span>
												{canManage && <Button
													isIconOnly
													size="sm"
													color="danger"
													variant="light"
													onPress={() => handleRemoveRow(row.id)}
												>
													<Trash2 className="w-4 h-4" />
												</Button>}
											</div>
											<div className="grid grid-cols-3 gap-2">
												<Input
													size="sm"
													isReadOnly={!canManage}
													label="Name"
													placeholder="e.g., Front Seats"
													value={row.name}
													onValueChange={val => handleRowChange(row.id, 'name', val)}
													isInvalid={isEmpty(row.name) || duplicateRowNames.has(row.name)}
													errorMessage={
														isEmpty(row.name)
															? 'Name is required'
															: duplicateRowNames.has(row.name)
																? 'Name must be unique'
																: null
													}
													isRequired
													variant="bordered"
												/>
												<Input
													size="sm"
													isReadOnly={!canManage}
													label="Seats"
													type="text"
													inputMode="numeric"
													value={row.seats}
													onValueChange={val => handleRowChange(row.id, 'seats', parseInt(val, 10) || 0)}
													variant="bordered"
												/>
												<Input
													size="sm"
													isReadOnly={!canManage}
													label="Arm (in)"
													type="text"
													inputMode="decimal"
													placeholder="0.00"
													value={row.arm}
													onValueChange={val => handleRowChange(row.id, 'arm', val)}
													isInvalid={hasFieldError(row.arm, true)}
													errorMessage={getFieldError(row.arm, true)}
													isRequired
													variant="bordered"
												/>
											</div>
										</div>
									))}
								</div>
							)}
						</div>
					</CardBody>
				</Card>

				{/* Right Column: Fuel & Envelope Card (Cyan/Indigo gradient) */}
				<Card className="border border-default-200 shadow-md bg-gradient-to-br from-white to-slate-50/20">
					<CardHeader className="bg-gradient-to-r from-cyan-100/80 via-cyan-50/50 to-transparent pb-3 border-b border-cyan-100/50">
						<div className="flex items-center gap-3">
							<div className="p-2 rounded-lg bg-cyan-500/10 ring-1 ring-cyan-500/20">
								<Fuel className="w-5 h-5 text-cyan-600" />
							</div>
							<span className="text-lg font-semibold text-cyan-900">Fuel & Envelope</span>
						</div>
					</CardHeader>
					<CardBody className="pt-4 space-y-5">
						{/* Section: Fuel Configuration */}
						<div>
							<div className="mb-5">
								<h4 className="text-xs font-semibold text-default-400 uppercase tracking-wider flex items-center gap-2">
									<span className="w-1 h-4 bg-cyan-400 rounded-full" />
									Fuel Configuration
								</h4>
							</div>
							<div className="space-y-3">
								<Input
									isReadOnly={!canManage}
									label="Consumption (lb/hr)"
									type="text"
									inputMode="decimal"
									placeholder="0.00"
									size="sm"
									value={fuelData.consumption}
									onValueChange={val => handleFuelChange('consumption', val)}
									isInvalid={hasFieldError(fuelData.consumption, true)}
									errorMessage={getFieldError(fuelData.consumption, true)}
									isRequired
									variant="bordered"
								/>
								<Input
									isReadOnly={!canManage}
									label="Density (lbs/gal)"
									type="text"
									inputMode="decimal"
									placeholder="6.0"
									size="sm"
									value={fuelData.density}
									onValueChange={val => handleFuelChange('density', val)}
									isInvalid={hasFieldError(fuelData.density, true)}
									errorMessage={getFieldError(fuelData.density, true)}
									isRequired
									variant="bordered"
								/>
								<Input
									isReadOnly={!canManage}
									label="Reserve (min)"
									type="text"
									inputMode="numeric"
									placeholder="30"
									size="sm"
									value={fuelData.reserve}
									onValueChange={val => handleFuelChange('reserve', val)}
									variant="bordered"
								/>

								{/* Tank Type */}
								<RadioGroup
									label="Tank Type"
									orientation="horizontal"
									isDisabled={!canManage}
									value={fuelData.tankType}
									onValueChange={value => {
										handleFuelChange('tankType', value);
										if (value === 'dynamic' && !fuelData.slidingFuelTankId && fuelTankModels.length > 0) {
											handleFuelChange('slidingFuelTankId', fuelTankModels[0].id);
										}
									}}
									size="sm"
									classNames={{
										label: 'text-sm text-default-700',
										wrapper: 'gap-6'
									}}
								>
									<Radio value="static" classNames={{ label: '!inline !mb-0 !font-normal' }}>Static</Radio>
									<Radio value="dynamic" classNames={{ label: '!inline !mb-0 !font-normal' }}>Dynamic</Radio>
								</RadioGroup>

								{/* Tank configuration (conditional) */}
								{fuelData.tankType === 'static' ? (
									<div className="grid grid-cols-2 gap-3">
										<Input
											isReadOnly={!canManage}
											label="Capacity (gal)"
											type="text"
											inputMode="decimal"
											placeholder="0.00"
											size="sm"
											value={fuelData.capacity}
											onValueChange={val => handleFuelChange('capacity', val)}
											isInvalid={hasFieldError(fuelData.capacity, true)}
											errorMessage={getFieldError(fuelData.capacity, true)}
											isRequired
											variant="bordered"
										/>
										<Input
											isReadOnly={!canManage}
											label="Arm (in)"
											type="text"
											inputMode="decimal"
											placeholder="0.00"
											size="sm"
											value={fuelData.arm}
											onValueChange={val => handleFuelChange('arm', val)}
											isInvalid={hasFieldError(fuelData.arm, true)}
											errorMessage={getFieldError(fuelData.arm, true)}
											isRequired
											variant="bordered"
										/>
									</div>
								) : (
									<Select
										label="Tank Model"
										placeholder="Select a fuel tank model"
										size="sm"
										isDisabled={!canManage}
										selectedKeys={fuelData.slidingFuelTankId ? [String(fuelData.slidingFuelTankId)] : []}
										onSelectionChange={keys => {
											const selectedKey = Array.from(keys)[0];
											handleFuelChange('slidingFuelTankId', selectedKey ? parseInt(selectedKey, 10) : null);
										}}
										variant="bordered"
										isRequired
									>
										{fuelTankModels.map(tank => (
											<SelectItem key={String(tank.id)} textValue={tank.model || `Tank ${tank.id}`}>
												{tank.model || `Tank ${tank.id}`}
											</SelectItem>
										))}
									</Select>
								)}

								{fuelData.tankType === 'dynamic' && (
									<Button
										size="sm"
										variant="bordered"
										startContent={canManage ? <Edit className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
										onPress={handleEditFuelTank}
									>
										{canManage ? 'Edit Fuel Tank' : 'View Fuel Tank'}
									</Button>
								)}

								{/* Pilot seat position */}
								<div className="flex items-center justify-between">
									<span className="text-sm font-medium text-default-700">Pilot seat on right side</span>
									<Switch
										isSelected={fuelData.pilotRight}
										onValueChange={value => handleFuelChange('pilotRight', value)}
										size="sm"
										isDisabled={!canManage}
									/>
								</div>
							</div>
						</div>

						<Divider className="my-4" />

						{/* Section: W&B Envelope Image */}
						<div>
							<div className="mb-5">
								<h4 className="text-xs font-semibold text-default-400 uppercase tracking-wider flex items-center gap-2">
									<span className="w-1 h-4 bg-indigo-400 rounded-full" />
									W&B Envelope Image
								</h4>
							</div>

							{/* Show image preview if exists, otherwise show upload zone */}
							{getImagePreview() ? (
								<div className="relative">
									<img
										src={getImagePreview()}
										alt="W&B Envelope"
										className="w-full max-h-[400px] object-contain rounded-lg border border-default-200"
									/>
									{canManage && <Button
										isIconOnly
										size="sm"
										color="danger"
										variant="flat"
										className="absolute top-2 right-2"
										onPress={handleRemoveImage}
									>
										<X className="w-4 h-4" />
									</Button>}
								</div>
							) : canManage ? (
								<div
									className="w-full p-6 border border-dashed border-default-300 rounded-lg cursor-pointer hover:border-primary-500 hover:bg-primary-50 transition-colors"
									onClick={() => fileInputRef.current?.click()}
									onDragOver={handleDragOver}
									onDragLeave={handleDragLeave}
									onDrop={handleDrop}
								>
									<div className="flex flex-col items-center justify-center py-4">
										<div className="p-3 rounded-lg bg-indigo-500/10 ring-1 ring-indigo-500/20 mb-3">
											<ImagePlus className="w-6 h-6 text-indigo-500" />
										</div>
										<p className="text-default-600 text-sm mb-1">Upload W&B Envelope Image</p>
										<p className="text-xs text-default-400">Click to browse or drag and drop</p>
										<p className="text-xs text-default-400 mt-1">JPEG, PNG, GIF, WebP (max 5MB)</p>
									</div>
								</div>
							) : (
								<p className="text-default-400 text-sm py-2 italic">No image uploaded</p>
							)}

							{/* Hidden file input */}
							<input
								ref={fileInputRef}
								type="file"
								accept="image/jpeg,image/png,image/gif,image/webp"
								onChange={handleImageUpload}
								className="hidden"
							/>
						</div>
					</CardBody>
				</Card>
			</div>

			{/* Action Buttons */}
			<div className="flex justify-end gap-4 mt-8">
				<Button
					variant="bordered"
					startContent={canManage ? <Edit className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
					onPress={handleEditEnvelope}
				>
					{canManage ? 'Edit W&B Envelope' : 'View W&B Envelope'}
				</Button>
				{canManage && (
					<Button
						color="success"
						startContent={<Save className="w-4 h-4" />}
						onPress={handleSave}
						isLoading={saving}
						isDisabled={hasValidationErrors() || saving}
					>
						Save Weight & Balance
					</Button>
				)}
			</div>
		</div>
	);
}

function WeightBalancePage() {
	return (
		<UnsavedChangesGuard>
			<WeightBalancePageContent />
		</UnsavedChangesGuard>
	);
}

export default WeightBalancePage;
